<template>
  <section
    class="top-section"
    :class="classes"
    tabindex="0"
    role="region"
    :aria-label="fullAccessibilityText"
  >
    <div class="top-section--cont" role="img" :aria-label="topsectionAltText">
      <div class="container-fluid l2">
        <div class="row">
          <div
            class="col-12 relative z-50"
            :class="{
              'col-12': !weatherWidgetEnabled,
              'col-12 col-xl-6 col-xxl-7': weatherWidgetEnabled,
              'text-center pt-20': center,
            }"
          >
            <h1 v-html="headline" />
            <p v-if="hasSlotDefault" ref="slotContent">
              <slot />
            </p>
          </div>
          <template v-if="weatherWidgetEnabled">
            <div class="d-none d-xl-block col-xl-1 col-xxl-1" />
            <div class="d-none d-xl-block col-xl-5 col-xxl-3">
              <io-weather-widget
                :postcode="weatherPostcode"
                :endpoint="weatherEndpoint"
              />
            </div>
          </template>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { storeToRefs } from 'pinia';
import IoWeatherWidget from './IoWeatherWidget';

import { useCenterPwaStore } from '@/apps/center/stores/pwa';

export default {
  name: 'IoSectionTop',

  components: {
    IoWeatherWidget,
  },

  props: {
    headline: {
      required: false,
      type: String,
      default: () => '',
    },
    showWeatherWidget: {
      required: false,
      type: Boolean,
      default: () => false,
    },
    weatherPostcode: {
      required: false,
      type: String,
      default: () => null,
    },
    weatherEndpoint: {
      required: false,
      type: String,
      default: () => null,
    },
    center: {
      type: Boolean,
      default: () => false,
    },
    topsectionImage: {
      required: false,
      type: String,
      default: () => null,
    },
    topsectionAltText: {
      required: false,
      type: String,
      default: () => null,
    },
  },

  setup() {
    const pwaStore = useCenterPwaStore();
    const { isActive: pwaActive, isDesktop } = storeToRefs(pwaStore);

    return {
      pwaActive,
      isDesktop,
    };
  },

  data() {
    return {
      $cont: null,
      color: null,
      rgb: null,
      contrastColor: null,
      optimizedReadability: this.IOH.recVal(window.IOC, 'config.topsection.optimizedReadability', false),
      slotText: '',
    };
  },

  computed: {
    weatherWidgetEnabled() {
      return this.showWeatherWidget === true
        && this.weatherPostcode !== null
        && this.weatherEndpoint !== null;
    },
    classes() {
      const classes = [];

      if (this.optimizedReadability) {
        classes.push('optimized-readability');
        classes.push(this.contrastColor);
      }

      if (this.pwaActive && !this.isDesktop) {
        classes.push('pwa');
      }

      return classes;
    },
    hasSlotDefault() {
      return this.$slots && this.$slots.default;
    },
    headlineText() {
      if (!this.headline) return '';
      // Strip HTML tags for accessibility
      const div = document.createElement('div');
      div.innerHTML = this.headline;
      return div.textContent || div.innerText || '';
    },
    fullAccessibilityText() {
      const parts = [];
      if (this.headlineText) {
        parts.push(this.headlineText);
      }
      if (this.slotText) {
        parts.push(this.slotText);
      }
      return parts.length > 0 ? parts.join('. ') : 'Top section content';
    },
  },

  mounted() {
    this.$cont = this.$el.querySelector('.top-section--cont');

    if (typeof requestAnimationFrame !== 'undefined') {
      requestAnimationFrame(this.parallax);
    } else if (typeof webkitRequestAnimationFrame !== 'undefined') {
      webkitRequestAnimationFrame(this.parallax);
    } else {
      window.addEventListener('scroll', this.parallax);
    }

    this.getColors();
    this.updateSlotText();
  },

  updated() {
    this.updateSlotText();
  },

  methods: {
    parallax() {
      const offsetY = window.scrollY;

      this.$cont.style.transform = `translateY(${Math.floor(offsetY / 5 * 3)}px)`;

      if (typeof requestAnimationFrame !== 'undefined') {
        requestAnimationFrame(this.parallax);
      } else if (typeof webkitRequestAnimationFrame !== 'undefined') {
        webkitRequestAnimationFrame(this.parallax);
      }
    },

    getColors() {
      this.color = window.getComputedStyle(this.$cont).getPropertyValue('color');
      this.rgb = this.color.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/);

      this.getContrastColor();
    },

    getContrastColor() {
      const uicolors = [this.rgb[1] / 255, this.rgb[2] / 255, this.rgb[3] / 255];
      const c = uicolors.map((col) => {
        if (col <= 0.03928) {
          return col / 12.92;
        }
        return ((col + 0.055) / 1.055) ** 2.4;
      });

      const L = (0.2126 * c[0]) + (0.7152 * c[1]) + (0.0722 * c[2]);
      this.contrastColor = (L > 0.179) ? 'black' : 'white';
    },

    updateSlotText() {
      if (this.$refs.slotContent) {
        this.slotText = this.$refs.slotContent.textContent || this.$refs.slotContent.innerText || '';
      }
    },
  },
};
</script>

<style>
.top-section-image {
  position: absolute;
  width: 100%;
  z-index: 0;
  top: 0;
}
</style>
